<template>
  <div class="messages">
    <admin-view-header title="Ogłoszenia">
      <div class="buttons">
        <protected :with="Permission.CREATE_MESSAGE">
          <button class="button is-success" @click="showNewMessageModal">
            <icon name="fa-plus" />
            <span>Nowe ogłoszenie</span>
          </button>
        </protected>
      </div>
    </admin-view-header>
    <div class="tabs">
      <ul>
        <li :class="{ 'is-active': tab === 'all' }">
          <a @click="selectTab('all')">
            <icon name="fa-list" class="is-small" />
            <span>Wszystkie</span>
          </a>
        </li>
        <li :class="{ 'is-active': tab === 'favored' }">
          <a @click="selectTab('favored')">
            <icon name="fa-star" class="is-small" />
            <span>Wyróżnione</span>
          </a>
        </li>
      </ul>
    </div>
    <div v-if="tab === 'all'">
      <message-box class="is-info" title="Informacja" icon="fa-info">
        Przekreślona data (np.
        <span class="hidden-date">14-11-2019, 09:30:17</span>) oznacza, że data
        widoczna jest tylko w panelu administracyjnym i jest całkowicie ukryta
        przed użytkownikiem. Niezależnie jednak od tego czy data jest ukryta,
        ogłoszenia są wciąż względem niej sortowane.
      </message-box>
      <loader :state="actions.messages" without-loading>
        <table class="table is-fullwidth" :key="pageable.page">
          <thead>
            <tr>
              <th class="expanded">Tytuł ogłoszenia</th>
              <th>Data</th>
              <th class="nowrap">Akcja</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="message in messages" :key="message.id">
              <td class="expanded">
                <router-link :to="`/messages/${message.id}`">{{
                  message.title
                }}</router-link>
              </td>
              <td :class="{ 'hidden-date': message.dateHidden }">
                <template v-if="message.date">{{
                  message.date.format("DD.MM.YYYY, HH:mm:ss")
                }}</template>
              </td>
              <td class="nowrap">
                <protected :with="Permission.UPDATE_MESSAGE">
                  <icon
                    class="icon-control"
                    :class="{ 'is-primary': message.favored }"
                    name="fa-star"
                    :data-tooltip="
                      message.favored
                        ? 'Usuń z wyróżnionych'
                        : 'Dodaj do wyróżnionych'
                    "
                    @click="toggleFavored(message)"
                  />
                </protected>
                <protected :with="Permission.UPDATE_MESSAGE">
                  <icon
                    class="icon-control"
                    data-tooltip="Edytuj ogłoszenie"
                    name="fa-pen"
                    @click="showEditMessageModal(message)"
                  />
                </protected>
                <protected :with="Permission.DELETE_MESSAGE">
                  <icon
                    class="icon-control is-danger"
                    name="fa-trash"
                    data-tooltip="Usuń ogłoszenie"
                    @click="showRemovalConfirmation(message)"
                  />
                </protected>
              </td>
            </tr>
          </tbody>
        </table>

        <template #error>
          <div class="grey-message">
            <icon name="fa-times-circle" />
            <p>Dane niedostępne z powodu braku połączenia z serwerem.</p>
          </div>
        </template>
      </loader>

      <pagination
        :pageable="pageable"
        @change-page="requestMessagesPage"
        :delta="2"
        buttons
      />
    </div>

    <div v-else>
      <message-box class="is-info" title="Informacja" icon="fa-info">
        Ogłoszenia oznaczone jako wyróżnione (
        <icon name="fa-star" class="has-text-primary" />
        ) poza panelem ogłoszeń zostaną również umieszczone na dole strony
        głównej oraz w stopce. Ze względów estetycznych liczba wyświetlanych
        ogłoszeń ograniczona została do {{ M.Favored.LIMIT }} najnowszych pod
        względem daty publikacji. Prezentowane ogłoszenia przedstawione zostały
        w poniższej tabelce.
      </message-box>
      <loader :state="actions.favoredMessages" without-loading>
        <table class="table is-fullwidth" :key="pageable.page">
          <thead>
            <tr>
              <th class="expanded">Tytuł wyróżnionego ogłoszenia</th>
              <th>Akcja</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="message in favoredMessages" :key="message.id">
              <td class="expanded">
                <router-link :to="`/messages/${message.id}`">{{
                  message.title
                }}</router-link>
              </td>
              <td>
                <protected :with="Permission.UPDATE_MESSAGE">
                  <icon
                    class="icon-control is-primary"
                    name="fa-star"
                    data-tooltip="Usuń z wyróżnionych"
                    @click="removeFavor(message.id)"
                  />
                </protected>
              </td>
            </tr>
          </tbody>
        </table>

        <template #error>
          <div class="grey-message">
            <icon name="fa-times-circle" />
            <p>Dane niedostępne z powodu braku połączenia z serwerem.</p>
          </div>
        </template>
      </loader>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import {
  REQUEST_MESSAGES,
  REQUEST_FAVORED_MESSAGES,
  ADD_MESSAGE,
  UPDATE_MESSAGE,
  DELETE_MESSAGE
} from ":actions";

import { YesNoCancelModal } from "%modals";
import { MessageModal } from "%modals";
import { SuccessNotification } from "%notifications";

import { Protected, WithDispatcherProgress } from "%mixins";

import M from "%const/messages";

import { Icon, Pagination, MessageBox } from "%helpers";
import AdminViewHeader from "%admin/components/global/AdminViewHeader";

export default {
  name: "messages",

  components: {
    Icon,
    Pagination,
    AdminViewHeader,
    MessageBox
  },

  mixins: [Protected, WithDispatcherProgress],

  data() {
    return {
      M,
      tab: "all"
    };
  },

  computed: {
    ...mapGetters(["messages"]),
    ...mapState({
      pageable: state => state.messages.pageable,
      favoredMessages: state => state.messages.favoredMessages
    })
  },

  mounted() {
    this.requestMessagesPage(1);
    this.requestFavoredMessages();
  },

  methods: {
    requestMessagesPage(page) {
      this.dispatchWithProgress("messages", REQUEST_MESSAGES, {
        showDate: true,
        page,
        size: 10
      });
    },

    requestFavoredMessages() {
      this.dispatchWithProgress("favoredMessages", REQUEST_FAVORED_MESSAGES);
    },

    showNewMessageModal() {
      this.$emit("show-modal", {
        component: MessageModal,
        props: {
          onSubmit: message => this.addMessage(message)
        }
      });
    },

    showEditMessageModal(message) {
      this.$emit("show-modal", {
        component: MessageModal,
        props: {
          message,
          onSubmit: m => this.updateMessage(message.id, m)
        }
      });
    },

    showRemovalConfirmation({ id, title }) {
      this.$emit("show-modal", {
        component: YesNoCancelModal,
        props: {
          title: "Potwierdzenie usunięcia",
          content: `
            Czy na pewno chcesz usunąć ogłoszenie o poniższym tytule?
            <blockquote>${title}</blockquote>
          `,
          proceed: {
            type: "is-danger",
            icon: "fa-trash",
            content: "Usuń",
            callback: () => this.removeMessage(id)
          },
          cancel: {
            content: "Anuluj"
          }
        }
      });
    },

    async removeMessage(id) {
      await this.$store.dispatch(DELETE_MESSAGE, { id });
      this.requestMessagesPage(this.pageable.page);
      this.requestFavoredMessages();
    },

    async addMessage(message) {
      return this.$store
        .dispatch(ADD_MESSAGE, { message })
        .then(response =>
          this.$emit("push-notification", {
            component: SuccessNotification,
            props: {
              content: `
            <p>Ogłoszenie o tytule <strong>${response.data.title}</strong> zostało opublikowane.</p>
          `
            }
          })
        )
        .then(() => this.requestMessagesPage(1))
        .then(() => this.requestFavoredMessages());
    },

    updateMessage(id, message) {
      return this.$store
        .dispatch(UPDATE_MESSAGE, {
          id,
          message
        })
        .then(response => {
          this.$emit("push-notification", {
            component: SuccessNotification,
            props: {
              content: `
            <p>Ogłoszenie o tytule <strong>${response.data.title}</strong> zostało zaktualizowane.</p>
          `
            }
          });

          this.requestMessagesPage(this.pageable.page);
          this.requestFavoredMessages();
        });
    },

    async toggleFavored(message) {
      await this.$store.dispatch(UPDATE_MESSAGE, {
        id: message.id,
        message: { favored: !message.favored }
      });

      this.requestMessagesPage(this.pageable.page);
      this.requestFavoredMessages();
    },

    async removeFavor(id) {
      await this.$store.dispatch(UPDATE_MESSAGE, {
        id,
        message: { favored: false }
      });

      this.requestMessagesPage(this.pageable.page);
      this.requestFavoredMessages();
    },

    selectTab(tab) {
      this.tab = tab;
    }
  }
};
</script>

<style lang="scss" scoped>
.title {
  width: 50%;
}

.hidden-date {
  color: $grey-light;
  text-decoration: line-through;
}
</style>
